/**
 * Sibling-tab help hook (ADR-0066; help findings I-004 and G-118, decision Q163, John 24 Oct 2026).
 *
 * <p>Some PB windows have tabs that PH10 serves as separate `/browse/<key>` routes (a browse
 * config's `tabs` group, e.g. w_lost_found_search's Contents, Cartons and Pallets tabs are
 * /browse/cyc-lost-found, -cartons and -pallets). PB has ONE help topic per window, so a group is
 * documented in ONE help file, normally its first tab's. A later tab with no help file of its own
 * shows that file ({@link resolveHelpRoute}); a `###` section whose heading ends in
 * `{tab=/browse/<later-tab>}` is that tab's own part of the group help, and the panel scrolls to
 * it when opened on that tab ({@link tabSectionAnchor}). The authoring convention is in
 * `help/README.md` and `docs/help_project/content-authoring-guide.md`.
 *
 * <p>Every group, in tab order, whether or not any tab has help yet: help written later for any
 * tab is picked up without touching this file. Static on purpose: `shared/` must not import
 * `features/browse/browseConfigs` (dependency-cruiser `shared-not-from-features`);
 * `features/browse/helpTabGroups.test.ts` derives the list from BROWSE_CONFIGS `tabs` and fails
 * on any drift.
 */
export const HELP_TAB_GROUPS: readonly (readonly string[])[] = [
  // w_lost_found_search (Contents / Cartons / Pallets)
  ['/browse/cyc-lost-found', '/browse/cyc-lost-found-cartons', '/browse/cyc-lost-found-pallets'],
  // w_container_condition (Pallet / Carton)
  ['/browse/cfg-pallet-cond', '/browse/cfg-carton-cond'],
  // putaway / pick / restock rule, class and group windows
  ['/browse/cfg-pa-rule', '/browse/cfg-pick-rule', '/browse/cfg-rstk-rule'],
  ['/browse/cfg-pa-class', '/browse/cfg-pick-class', '/browse/cfg-rstk-class'],
  ['/browse/cfg-pa-grp', '/browse/cfg-pick-grp', '/browse/cfg-rstk-grp'],
  // container types
  [
    '/browse/cfg-pallet-type',
    '/browse/cfg-carton-type',
    '/browse/cfg-tote-type',
    '/browse/cfg-carton-type-byslot',
    '/browse/cfg-carton-group',
    '/browse/cfg-carton-category',
  ],
  // reason codes
  [
    '/browse/cfg-reason-adjusted',
    '/browse/cfg-reason-held',
    '/browse/cfg-reason-blocked',
    '/browse/cfg-exception-type',
    '/browse/cfg-reason-return',
    '/browse/cfg-reason-prerec',
    '/browse/cfg-reason-activity',
  ],
  // translations
  ['/browse/cfg-translate', '/browse/cfg-translate-sum'],
  ['/browse/cfg-mtranslate1', '/browse/cfg-mtranslate2', '/browse/cfg-mtranslate3'],
  // eEnterprise current / history tabs
  ['/browse/ee-adjustments', '/browse/ee-adjustments-hist'],
  ['/browse/ee-asns', '/browse/ee-asns-hist'],
  ['/browse/ee-receipts', '/browse/ee-receipts-hist'],
  ['/browse/ee-orders', '/browse/ee-orders-hist'],
];

/** The tab group `route` belongs to (tab order), or undefined when it is no sibling tab. */
export function tabGroupOf(
  route: string,
  groups: readonly (readonly string[])[] = HELP_TAB_GROUPS,
): readonly string[] | undefined {
  return groups.find((g) => g.includes(route));
}

/** `### Cartons tab {tab=/browse/cyc-lost-found-cartons}`: the heading attribute, at the end. */
export const HELP_TAB_ATTR_PATTERN = /\s*\{tab=(\/[^}\s]+)\}\s*$/;

/** Split a `###` heading into its text and its `{tab=…}` route (undefined when none). */
export function parseTabHeading(heading: string): { heading: string; tab?: string } {
  const m = HELP_TAB_ATTR_PATTERN.exec(heading);
  if (!m?.[1]) return { heading };
  return { heading: heading.slice(0, m.index).trim(), tab: m[1] };
}

/**
 * The anchor of the section that documents the live tab, or null: only when the help shown is
 * another route's (the group's file), and that file has a section tagged for this pathname.
 */
export function tabSectionAnchor(
  pathname: string,
  helpRoute: string | null | undefined,
  sections: readonly { anchor: string; tab?: string }[],
): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (!helpRoute || helpRoute === path) return null;
  return sections.find((s) => s.tab === path)?.anchor ?? null;
}
